import { createApp } from 'vue'
import VueClipboard from 'vue3-clipboard'
import nuxtUi from '@nuxt/ui/vue-plugin'
import * as Sentry from '@sentry/vue'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'

import App from './App.vue'
import router from './router'
import pinia from './stores'

import { currency, number } from './utils/formatters'
import {
    registerPreloadErrorRecovery,
    clearPreloadErrorGuard,
} from './helpers/preload-error-recovery'

/* Components */
import ZenEmptyState from './screens/_components/EmptyState.vue'

/* Design System */
import ZenContainer from './screens/_system/Container.vue'
import ZenDateTime from './screens/_system/DateTime.vue'
import ZenDeniedState from './screens/_system/DeniedState.vue'
import ZenDropdownMenu from './screens/_system/DropdownMenu.vue'
import ZenEmptyTableMessage from './screens/_system/EmptyTableMessage.vue'
import ZenInlinePill from './screens/_system/InlinePill.vue'
import ZenLoadingIndicator from './screens/_system/LoadingIndicator.vue'
import ZenMessageBlock from './screens/_system/MessageBlock.vue'
import ZenSkeleton from './screens/_system/Skeleton.vue'
import ZenTooltip from './screens/_system/Tooltip.vue'

/* Design System: Buttons */
import * as buttons from './components/ui/buttons'

/* Design System: Grid */
import * as grids from './components/ui/grid'

/* Design System: Icons */
import * as icons from './screens/_system/Icons'

/* Design System: Input */
import * as inputs from './screens/_system/Inputs'

/* Design System: Modals */
import * as modals from './components/ui/modals'

/* Design System: UI */
import * as ui from './components/ui'

import './index.css'

registerPreloadErrorRecovery()

const app = createApp(App)
    /* Components */
    .component('ZenEmptyState', ZenEmptyState)

    /* Design System */
    .component('ZenContainer', ZenContainer)
    .component('ZenDateTime', ZenDateTime)
    .component('ZenDeniedState', ZenDeniedState)
    .component('ZenDropdownMenu', ZenDropdownMenu)
    .component('ZenEmptyTableMessage', ZenEmptyTableMessage)
    .component('ZenInlinePill', ZenInlinePill)
    .component('ZenLoadingIndicator', ZenLoadingIndicator)
    .component('ZenMessageBlock', ZenMessageBlock)
    .component('ZenSkeleton', ZenSkeleton)
    .component('ZenTooltip', ZenTooltip)

    /* Design System: Buttons */
    .component('ZenAddButton', buttons.ZenAddButton)
    .component('ZenTableIcon', buttons.ZenTableIcon)

    /* Design System: Grid */
    .component('ZenGridBody', grids.ZenGridBody)
    .component('ZenGridBodyCell', grids.ZenGridBodyCell)
    .component('ZenGridBodyCellPrimaryLink', grids.ZenGridBodyCellPrimaryLink)
    .component('ZenGridBodyRow', grids.ZenGridBodyRow)
    .component('ZenGrid', grids.ZenGrid)
    .component('ZenGridFooter', grids.ZenGridFooter)
    .component('ZenGridFooterCell', grids.ZenGridFooterCell)
    .component('ZenGridFooterRow', grids.ZenGridFooterRow)
    .component('ZenGridHeader', grids.ZenGridHeader)
    .component('ZenGridHeaderCell', grids.ZenGridHeaderCell)
    .component('ZenGridHeaderRow', grids.ZenGridHeaderRow)
    .component('ZenGridMobileDetail', grids.ZenGridMobileDetail)
    .component('ZenGridMobileDetailItem', grids.ZenGridMobileDetailItem)
    .component('ZenGridTable', grids.ZenGridTable)

    /* Design System: UI */
    .component('ZenButton', ui.ZenButton)
    .component('ZenTabList', ui.ZenTabList)
    .component('ZenTab', ui.ZenTab)
    .component('ZenPagination', ui.ZenPagination)
    .component('ZenJsonEditor', ui.ZenJsonEditor)
    .component('ZenJsonDiff', ui.ZenJsonDiff)

    /* Design System: Icons */
    .component('ZenIconAlertWarning', icons.ZenIconAlertWarning)
    .component('ZenIconBoxChecked', icons.ZenIconBoxChecked)
    .component('ZenIconBoxEmpty', icons.ZenIconBoxEmpty)
    .component('ZenIconChartBar', icons.ZenIconChartBar)
    .component('ZenIconChevronDown', icons.ZenIconChevronDown)
    .component('ZenIconChevronRight', icons.ZenIconChevronRight)
    .component('ZenIconChevronUp', icons.ZenIconChevronUp)
    .component('ZenIconClock', icons.ZenIconClock)
    .component('ZenIconClose', icons.ZenIconClose)
    .component('ZenIconCreate', icons.ZenIconCreate)
    .component('ZenIconDownload', icons.ZenIconDownload)
    .component('ZenIconEmailShare', icons.ZenIconEmailShare)
    .component('ZenIconFavorite', icons.ZenIconFavorite)
    .component('ZenIconFilter', icons.ZenIconFilter)
    .component('ZenIconFlag', icons.ZenIconFlag)
    .component('ZenIconFolder', icons.ZenIconFolder)
    .component('ZenIconHistory', icons.ZenIconHistory)
    .component('ZenIconInfo', icons.ZenIconInfo)
    .component('ZenIconOpenLink', icons.ZenIconOpenLink)
    .component('ZenIconRemove', icons.ZenIconRemove)
    .component('ZenIconServer', icons.ZenIconServer)
    .component('ZenIconShieldLock', icons.ZenIconShieldLock)
    .component('ZenIconTarget', icons.ZenIconTarget)
    .component('ZenIconTerminal', icons.ZenIconTerminal)
    .component('ZenIconToggling', icons.ZenIconToggling)
    .component('ZenIconToken', icons.ZenIconToken)
    .component('ZenIconUpdate', icons.ZenIconUpdate)
    .component('ZenIconUser', icons.ZenIconUser)
    .component('ZenIconUsers', icons.ZenIconUsers)
    .component('ZenIconWebhook', icons.ZenIconWebhook)
    .component('ZenIconWorld', icons.ZenIconWorld)

    /* Design System: Inputs */
    .component('ZenAutoComplete', inputs.ZenAutoComplete)
    .component('ZenSelect', inputs.ZenSelect)

    /* Design System: Modals */
    .component('ZenMessageModal', modals.ZenMessageModal)
    .component('ZenModal', modals.ZenModal)
    .component('ZenSlideIn', modals.ZenSlideIn)

    .use(pinia)
    .use(router)
    .use(VueClipboard, {
        autoSetContainer: true,
        appendToBody: true,
    })

    .use(Toast, {
        transition: 'Vue-Toastification__bounce',
        maxToasts: 20,
        newestOnTop: true,
    })

    .use(nuxtUi)

Sentry.init({
    app,
    dsn: 'https://dc7f4ee98937ce80d188b4dbf44ad340@o4511464902492160.ingest.us.sentry.io/4511464910028800',
    environment: import.meta.env.PROD ? 'production' : 'local',
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
        'localhost',
        /^https:\/\/api\.zenmanage\.com\/internal/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

app.config.globalProperties.$filters = {
    currency,
    number,
}

app.mount('#app')

// Only clear the reload guard once the initial navigation has actually
// succeeded — if it fails (e.g. because of the very chunk-load failure the
// guard is protecting against), leave the guard set so a second
// preloadError from this same broken load doesn't trigger another reload.
router
    .isReady()
    .then(clearPreloadErrorGuard)
    .catch(() => {})
